import React, { useState } from 'react';
import { IoClose, IoArrowBack, IoArrowForward } from 'react-icons/io5';
import PropTypes from 'prop-types';
import AxiosInstance from 'axiosConfig';

const NodeMaintenanceModal = ({
  isOpen,
  onClose,
  onSubmit,
  formData,
  setFormData,
  errors,
  setErrors,
  onRetrieveNode,
}) => {
  const [activeTab, setActiveTab] = useState('addNode');
  const [uploadResponse, setUploadResponse] = useState('');
  const [reviewNodeResponse, setReviewNodeResponse] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (activeTab === 'addNode') {
      if (!formData.node_key_words) newErrors.node_key_words = 'Node keywords are required';
      if (!formData.ans_desc) newErrors.ans_desc = 'Answer description is required';
      if (!formData.que_topic) newErrors.que_topic = 'Question topic is required';
      if (!formData.bot_act_as) newErrors.bot_act_as = 'Bot role is required';
      if (!formData.que_category) newErrors.que_category = 'Category is required';
      if (!formData.culture) newErrors.culture = 'Culture is required';
    } else if (activeTab === 'reviewNode') {
      if (!formData.node_number) newErrors.node_number = 'Node number is required';
      else if (!/^\d+$/.test(formData.node_number)) newErrors.node_number = 'Node number must be digits';
    } else if (activeTab === 'uploadBulkNodes') {
      if (!formData.bulkFile) {
        newErrors.bulkFile = 'Please select a file to upload';
      } else if (!formData.bulkFile.name.match(/\.(xlsx|xls)$/i)) {
        newErrors.bulkFile = 'Only Excel files (.xlsx, .xls) are allowed';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleRetrieve = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        const response = await AxiosInstance.get(`/node-retrieve-${formData.node_number}`);
        if (response?.data?.message && typeof response.data.message === 'object') {
          setReviewNodeResponse(response.data);
          setEditFormData(response.data.message);
          onRetrieveNode(formData.node_number);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        setErrors(prev => ({ ...prev, node_number: `Failed to retrieve node: ${error.message}` }));
        setReviewNodeResponse(null);
        setEditFormData({});
      }
    }
  };

  const handleNextNode = async (e) => {
    e.preventDefault();
    const currentNodeNumber = parseInt(formData.node_number, 10);
    if (isNaN(currentNodeNumber)) {
      setErrors(prev => ({ ...prev, node_number: 'Current node number is invalid' }));
      return;
    }
    const nextNodeNumber = currentNodeNumber + 1;
    setFormData(prev => ({ ...prev, node_number: nextNodeNumber.toString() }));
    setErrors(prev => ({ ...prev, node_number: '' }));
    try {
      const response = await AxiosInstance.get(`/node-retrieve-${nextNodeNumber}`);
      if (response?.data?.message && typeof response.data.message === 'object') {
        setReviewNodeResponse(response.data);
        setEditFormData(response.data.message);
        onRetrieveNode(nextNodeNumber.toString());
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, node_number: `Failed to retrieve node ${nextNodeNumber}: ${error.message}` }));
      setReviewNodeResponse(null);
      setEditFormData({});
    }
  };

  const handlePreviousNode = async (e) => {
    e.preventDefault();
    const currentNodeNumber = parseInt(formData.node_number, 10);
    if (isNaN(currentNodeNumber)) {
      setErrors(prev => ({ ...prev, node_number: 'Current node number is invalid' }));
      return;
    }
    const previousNodeNumber = currentNodeNumber - 1;
    if (previousNodeNumber < 1) {
      setErrors(prev => ({ ...prev, node_number: 'Cannot navigate to a node number less than 1' }));
      return;
    }
    setFormData(prev => ({ ...prev, node_number: previousNodeNumber.toString() }));
    setErrors(prev => ({ ...prev, node_number: '' }));
    try {
      const response = await AxiosInstance.get(`/node-retrieve-${previousNodeNumber}`);
      if (response?.data?.message && typeof response.data.message === 'object') {
        setReviewNodeResponse(response.data);
        setEditFormData(response.data.message);
        onRetrieveNode(previousNodeNumber.toString());
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, node_number: `Failed to retrieve node ${previousNodeNumber}: ${error.message}` }));
      setReviewNodeResponse(null);
      setEditFormData({});
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await AxiosInstance.post(`/node-update-${formData.node_number}`, { message: editFormData });
      setReviewNodeResponse(prev => ({ ...prev, message: editFormData }));
      setIsEditing(false);
    } catch (error) {
      setErrors(prev => ({ ...prev, edit: `Failed to update node: ${error.message}` }));
    }
  };

  const handleReviewAnother = () => {
    setFormData(prev => ({ ...prev, node_number: '' }));
    setErrors(prev => ({ ...prev, node_number: '' }));
    setReviewNodeResponse(null);
    setEditFormData({});
    setIsEditing(false);
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        const formDataToSend = new FormData();
        formDataToSend.append('file', formData.bulkFile);
        const response = await AxiosInstance.post('/upload-bulk-nodes', formDataToSend);
        setUploadResponse(JSON.stringify(response.data, null, 2));
      } catch (error) {
        setUploadResponse(`Error uploading file: ${error.message}`);
      }
    }
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      bulkFile: e.target.files[0]
    }));
    setErrors(prev => ({ ...prev, bulkFile: '' }));
    setUploadResponse('');
  };

  const insertVariable = (fieldName, variable) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: (prev[fieldName] || '') + variable,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-md w-full max-w-2xl max-h-[80vh] flex flex-col overflow-y-auto">
        <div className="flex justify-between items-center p-6 pb-0">
          <div></div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
            aria-label="Close modal"
          >
            <IoClose size={24} />
          </button>
        </div>
        
        <div className="flex border-b">
          <button
            className={`tab-button px-4 py-2 font-medium ${
              activeTab === 'addNode'
                ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                : 'bg-gray-100 text-gray-700'
            }`}
            onClick={() => setActiveTab('addNode')}
          >
            Add Node
          </button>
          <button
            className={`tab-button px-4 py-2 font-medium ${
              activeTab === 'reviewNode'
                ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                : 'bg-gray-100 text-gray-700'
            }`}
            onClick={() => setActiveTab('reviewNode')}
          >
            Review Node
          </button>
          <button
            className={`tab-button px-4 py-2 font-medium ${
              activeTab === 'uploadBulkNodes'
                ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                : 'bg-gray-100 text-gray-700'
            }`}
            onClick={() => setActiveTab('uploadBulkNodes')}
          >
            Upload Bulk Nodes
          </button>
        </div>

        <div className="p-6 flex-1">
          {activeTab === 'addNode' ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <fieldset className="mb-4">
                <legend className="text-lg font-semibold mb-2">Node Information</legend>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Node Keywords
                    {errors.node_key_words && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <textarea
                    name="node_key_words"
                    value={formData.node_key_words || ''}
                    onChange={handleInputChange}
                    rows={2}
                    maxLength={400}
                    className={`w-full p-2 border rounded-lg ${
                      errors.node_key_words ? 'border-red-500' : 'border-blue-300'
                    } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  />
                  {errors.node_key_words && (
                    <p className="text-red-500 text-sm mt-1">{errors.node_key_words}</p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Answer Description
                    {errors.ans_desc && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <textarea
                    name="ans_desc"
                    value={formData.ans_desc || ''}
                    onChange={handleInputChange}
                    rows={3}
                    maxLength={1000}
                    className={`w-full p-2 border rounded-lg ${
                      errors.ans_desc ? 'border-red-500' : 'border-blue-300'
                    } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  />
                  <span
                    className="text-blue-600 cursor-pointer underline text-sm mt-1 inline-block"
                    onClick={() => insertVariable('ans_desc', '/Name/')}
                  >
                    @FirstName
                  </span>
                  {errors.ans_desc && (
                    <p className="text-red-500 text-sm mt-1">{errors.ans_desc}</p>
                  )}
                </div>
              </fieldset>

              <fieldset className="mb-4">
                <legend className="text-lg font-semibold mb-2">Node Options</legend>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Question Topic
                      {errors.que_topic && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <select
                      name="que_topic"
                      value={formData.que_topic || ''}
                      onChange={handleInputChange}
                      className={`w-full p-2 border rounded-lg ${
                        errors.que_topic ? 'border-red-500' : 'border-blue-300'
                      } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    >
                      <option value="" disabled>Select Topic</option>
                      <option value="topic1">Topic 1</option>
                      <option value="topic2">Topic 2</option>
                      <option value="topic3">Topic 3</option>
                    </select>
                    {errors.que_topic && (
                      <p className="text-red-500 text-sm mt-1">{errors.que_topic}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bot Act As
                      {errors.bot_act_as && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <select
                      name="bot_act_as"
                      value={formData.bot_act_as || ''}
                      onChange={handleInputChange}
                      className={`w-full p-2 border rounded-lg ${
                        errors.bot_act_as ? 'border-red-500' : 'border-blue-300'
                      } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    >
                      <option value="" disabled>Select Role</option>
                      <option value="assistant">Assistant</option>
                      <option value="advisor">Advisor</option>
                      <option value="expert">Expert</option>
                    </select>
                    {errors.bot_act_as && (
                      <p className="text-red-500 text-sm mt-1">{errors.bot_act_as}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Question Category
                      {errors.que_category && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <select
                      name="que_category"
                      value={formData.que_category || ''}
                      onChange={handleInputChange}
                      className={`w-full p-2 border rounded-lg ${
                        errors.que_category ? 'border-red-500' : 'border-blue-300'
                      } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    >
                      <option value="" disabled>Select Category</option>
                      <option value="general">General</option>
                      <option value="technical">Technical</option>
                      <option value="specific">Specific</option>
                    </select>
                    {errors.que_category && (
                      <p className="text-red-500 text-sm mt-1">{errors.que_category}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Culture
                      {errors.culture && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <select
                      name="culture"
                      value={formData.culture || ''}
                      onChange={handleInputChange}
                      className={`w-full p-2 border rounded-lg ${
                        errors.culture ? 'border-red-500' : 'border-blue-300'
                      } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    >
                      <option value="" disabled>Select Culture</option>
                      <option value="global">Global</option>
                      <option value="regional">Regional</option>
                      <option value="local">Local</option>
                    </select>
                    {errors.culture && (
                      <p className="text-red-500 text-sm mt-1">{errors.culture}</p>
                    )}
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="enforces_match"
                      checked={formData.enforces_match || false}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label className="ml-2 text-sm text-gray-700">
                      Enforces Match
                    </label>
                  </div>
                </div>
              </fieldset>

              <fieldset className="mb-4">
                <legend className="text-lg font-semibold mb-2">Additional Details</legend>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Comments
                  </label>
                  <textarea
                    name="comments"
                    value={formData.comments || ''}
                    onChange={handleInputChange}
                    rows={2}
                    maxLength={300}
                    className="w-full p-2 border rounded-lg border-blue-300 focus ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </fieldset>

              <button
                type="submit"
                className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Submit Node
              </button>
            </form>
          ) : activeTab === 'reviewNode' ? (
            <div className="space-y-4">
              {!reviewNodeResponse ? (
                <form onSubmit={handleRetrieve} className="space-y-4">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Node Number
                      {errors.node_number && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <input
                      type="text"
                      name="node_number"
                      value={formData.node_number || ''}
                      onChange={handleInputChange}
                      className={`w-full p-2 border rounded-lg ${
                        errors.node_number ? 'border-red-500' : 'border-blue-300'
                      } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                      placeholder="Enter node number"
                    />
                    {errors.node_number && (
                      <p className="text-red-500 text-sm mt-1">{errors.node_number}</p>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Retrieve Node
                  </button>
                </form>
              ) : (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={handlePreviousNode}
                      className="text-blue-600 hover:text-blue-800 focus:outline-none"
                      aria-label="Previous Node"
                    >
                      <IoArrowBack size={24} />
                    </button>
                    <h3 className="text-lg font-semibold">Details for Node {formData.node_number}</h3>
                    <button
                      onClick={handleNextNode}
                      className="text-blue-600 hover:text-blue-800 focus:outline-none"
                      aria-label="Next Node"
                    >
                      <IoArrowForward size={24} />
                    </button>
                  </div>
                  {reviewNodeResponse.message && typeof reviewNodeResponse.message === 'object' ? (
                    isEditing ? (
                      <form onSubmit={handleEditSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Question Key</label>
                            <input
                              type="text"
                              name="que_key"
                              value={editFormData.que_key || ''}
                              onChange={handleEditInputChange}
                              className="w-full p-2 border rounded-lg border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              readOnly
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Entry Status</label>
                            <input
                              type="checkbox"
                              name="entry_status"
                              checked={editFormData.entry_status || false}
                              onChange={handleEditInputChange}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-2"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Node Keywords</label>
                          <textarea
                            name="node_key_words"
                            value={editFormData.node_key_words || ''}
                            onChange={handleEditInputChange}
                            rows={2}
                            className="w-full p-2 border rounded-lg border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Answer Description</label>
                          <textarea
                            name="ans_desc"
                            value={editFormData.ans_desc || ''}
                            onChange={handleEditInputChange}
                            rows={3}
                            className="w-full p-2 border rounded-lg border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              name="enforces_match"
                              checked={editFormData.enforces_match || false}
                              onChange={handleEditInputChange}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label className="ml-2 text-sm text-gray-700">Enforces Match</label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              name="expects_yes_no"
                              checked={editFormData.expects_yes_no || false}
                              onChange={handleEditInputChange}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label className="ml-2 text-sm text-gray-700">Expects Yes No</label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              name="ans_in_convo_id"
                              checked={editFormData.ans_in_convo_id || false}
                              onChange={handleEditInputChange}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label className="ml-2 text-sm text-gray-700">Answer In Conversation</label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              name="stems_logic_in_num"
                              checked={editFormData.stems_logic_in_num || false}
                              onChange={handleEditInputChange}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label className="ml-2 text-sm text-gray-700">Stems Logic In Number</label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              name="is_action_trigger"
                              checked={editFormData.is_action_trigger || false}
                              onChange={handleEditInputChange}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label className="ml-2 text-sm text-gray-700">Is Action Trigger</label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              name="is_ussd_prompt"
                              checked={editFormData.is_ussd_prompt || false}
                              onChange={handleEditInputChange}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label className="ml-2 text-sm text-gray-700">Is USSD Prompt</label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              name="bot_process_flg"
                              checked={editFormData.bot_process_flg || false}
                              onChange={handleEditInputChange}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label className="ml-2 text-sm text-gray-700">Bot Process Flag</label>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Comments</label>
                            <input
                              type="text"
                              name="comments"
                              value={editFormData.comments || ''}
                              onChange={handleEditInputChange}
                              className="w-full p-2 border rounded-lg border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Question Category</label>
                            <input
                              type="text"
                              name="que_category"
                              value={editFormData.que_category || ''}
                              onChange={handleEditInputChange}
                              className="w-full p-2 border rounded-lg border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Create User</label>
                            <input
                              type="text"
                              name="create_usr"
                              value={editFormData.create_usr || ''}
                              onChange={handleEditInputChange}
                              className="w-full p-2 border rounded-lg border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Bot Act As</label>
                            <input
                              type="text"
                              name="bot_act_as"
                              value={editFormData.bot_act_as || ''}
                              onChange={handleEditInputChange}
                              className="w-full p-2 border rounded-lg border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Question Topic</label>
                            <input
                              type="text"
                              name="que_topic"
                              value={editFormData.que_topic || ''}
                              onChange={handleEditInputChange}
                              className="w-full p-2 border rounded-lg border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                        {errors.edit && (
                          <p className="text-red-500 text-sm mt-1">{errors.edit}</p>
                        )}
                        <div className="flex space-x-4">
                          <button
                            type="submit"
                            className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            Save Changes
                          </button>
                          <button
                            type="button"
                            onClick={() => setIsEditing(false)}
                            className="w-full bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-2 bg-white border border-gray-200 rounded-lg">
                            <span className="font-medium text-gray-700">Question Key:</span>
                            <span className="text-sm text-gray-600 ml-2">{reviewNodeResponse.message.que_key || ''}</span>
                          </div>
                          <div className="p-2 bg-white border border-gray-200 rounded-lg">
                            <span className="font-medium text-gray-700">Entry Status:</span>
                            <span className="text-sm text-gray-600 ml-2">{reviewNodeResponse.message.entry_status ? 'True' : 'False'}</span>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Node Keywords</label>
                          <div className="p-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-600">
                            {reviewNodeResponse.message.node_key_words || ''}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Answer Description</label>
                          <div className="p-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-600">
                            {reviewNodeResponse.message.ans_desc || ''}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex items-center p-2 bg-white border border-gray-200 rounded-lg">
                            <input
                              type="checkbox"
                              checked={reviewNodeResponse.message.enforces_match || false}
                              readOnly
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                            <label className="ml-2 text-sm font-medium text-gray-700">Enforces Match</label>
                          </div>
                          <div className="flex items-center p-2 bg-white border border-gray-200 rounded-lg">
                            <input
                              type="checkbox"
                              checked={reviewNodeResponse.message.expects_yes_no || false}
                              readOnly
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                            <label className="ml-2 text-sm font-medium text-gray-700">Expects Yes No</label>
                          </div>
                          <div className="flex items-center p-2 bg-white border border-gray-200 rounded-lg">
                            <input
                              type="checkbox"
                              checked={reviewNodeResponse.message.ans_in_convo_id || false}
                              readOnly
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                            <label className="ml-2 text-sm font-medium text-gray-700">Answer In Conversation</label>
                          </div>
                          <div className="flex items-center p-2 bg-white border border-gray-200 rounded-lg">
                            <input
                              type="checkbox"
                              checked={reviewNodeResponse.message.stems_logic_in_num || false}
                              readOnly
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                            <label className="ml-2 text-sm font-medium text-gray-700">Stems Logic In Number</label>
                          </div>
                          <div className="flex items-center p-2 bg-white border border-gray-200 rounded-lg">
                            <input
                              type="checkbox"
                              checked={reviewNodeResponse.message.is_action_trigger || false}
                              readOnly
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                            <label className="ml-2 text-sm font-medium text-gray-700">Is Action Trigger</label>
                          </div>
                          <div className="flex items-center p-2 bg-white border border-gray-200 rounded-lg">
                            <input
                              type="checkbox"
                              checked={reviewNodeResponse.message.is_ussd_prompt || false}
                              readOnly
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                            <label className="ml-2 text-sm font-medium text-gray-700">Is USSD Prompt</label>
                          </div>
                          <div className="flex items-center p-2 bg-white border border-gray-200 rounded-lg">
                            <input
                              type="checkbox"
                              checked={reviewNodeResponse.message.bot_process_flg || false}
                              readOnly
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                            <label className="ml-2 text-sm font-medium text-gray-700">Bot Process Flag</label>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-2 bg-white border border-gray-200 rounded-lg">
                            <span className="font-medium text-gray-700">Comments:</span>
                            <span className="text-sm text-gray-600 ml-2">{reviewNodeResponse.message.comments || ''}</span>
                          </div>
                          <div className="p-2 bg-white border border-gray-200 rounded-lg">
                            <span className="font-medium text-gray-700">Question Category:</span>
                            <span className="text-sm text-gray-600 ml-2">{reviewNodeResponse.message.que_category || ''}</span>
                          </div>
                          <div className="p-2 bg-white border border-gray-200 rounded-lg">
                            <span className="font-medium text-gray-700">Create User:</span>
                            <span className="text-sm text-gray-600 ml-2">{reviewNodeResponse.message.create_usr || ''}</span>
                          </div>
                          <div className="p-2 bg-white border border-gray-200 rounded-lg">
                            <span className="font-medium text-gray-700">Bot Act As:</span>
                            <span className="text-sm text-gray-600 ml-2">{reviewNodeResponse.message.bot_act_as || ''}</span>
                          </div>
                          <div className="p-2 bg-white border border-gray-200 rounded-lg">
                            <span className="font-medium text-gray-700">Question Topic:</span>
                            <span className="text-sm text-gray-600 ml-2">{reviewNodeResponse.message.que_topic || ''}</span>
                          </div>
                        </div>
                        <div className="flex space-x-4 mt-4">
                          <button
                            type="button"
                            onClick={() => setIsEditing(true)}
                            className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            Edit Current Node
                          </button>
                          <button
                            type="button"
                            onClick={handleReviewAnother}
                            className="w-full bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300"
                          >
                            Review Another Node
                          </button>
                        </div>
                      </div>
                    )
                  ) : (
                    <p className="text-red-500 text-sm">No valid node details available</p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleFileUpload} className="space-y-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload File
                  {errors.bulkFile && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input
                  type="file"
                  name="bulkFile"
                  onChange={handleFileChange}
                  accept=".xlsx,.xls"
                  className={`w-full p-2 border rounded-lg ${
                    errors.bulkFile ? 'border-red-500' : 'border-blue-300'
                  } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                />
                {errors.bulkFile && (
                  <p className="text-red-500 text-sm mt-1">{errors.bulkFile}</p>
                )}
              </div>

              <button
                type="submit"
                className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Submit File
              </button>

              {uploadResponse && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-lg font-semibold mb-2">Upload Response</h3>
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                    {uploadResponse}
                  </pre>
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

NodeMaintenanceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  setErrors: PropTypes.func.isRequired,
  onRetrieveNode: PropTypes.func.isRequired,
};

export default NodeMaintenanceModal;